import { useMutation } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { maskWholeNumber } from '@givebutter/util';
import { faBroomWide } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dismissAutoFormattedAddresses } from 'api/contacts/data-hygiene';
import * as WhatsThisDialog from 'assets/js/pages/contacts/data-hygiene/WhatsThisDialog';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useDataHygieneMetrics } from 'customHooks/useDataHygieneMetrics';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';

const AutoAddressesMetricCard = () => {
    const { data: metrics } = useDataHygieneMetrics();
    const { accountId } = useCurrentAccount();
    const autoAddressesTotal: number = metrics.data_hygiene.addresses.auto_formatted || 0;

    const { mutate: dismiss } = useMutation({
        mutationFn: () => dismissAutoFormattedAddresses(accountId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
        },
    });

    if (autoAddressesTotal <= 0) {
        return null;
    }

    return (
        <AlertNotification.Root
            type="info"
            closable={true}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            onClose={() => {
                dismiss();
            }}
        >
            <AlertNotification.Content>
                <div className="flex w-full items-start gap-4">
                    <BoxIcon size="md">
                        <FontAwesomeIcon className="text-xl" icon={faBroomWide} />
                    </BoxIcon>

                    <div className="flex flex-grow flex-col gap-3 text-sm text-gray-700">
                        <div>
                            <AlertNotification.Title className="font-semibold">
                                {maskWholeNumber(autoAddressesTotal)} addresses auto-formatted
                            </AlertNotification.Title>
                            <AlertNotification.Description className="text-gray-600">
                                We found and fixed these addresses for you during our routine check.
                            </AlertNotification.Description>
                        </div>
                        <div className="flex gap-3">
                            <AlertNotification.Close className="text-sm font-medium text-gray-600 hover:underline">
                                Dismiss
                            </AlertNotification.Close>
                            <WhatsThisDialog.Root>
                                <WhatsThisDialog.Trigger asChild>
                                    <Button
                                        variant="none"
                                        color="primary"
                                        size="sm"
                                        className="font-medium hover:underline"
                                    >
                                        <span>What's this?</span>
                                    </Button>
                                </WhatsThisDialog.Trigger>
                            </WhatsThisDialog.Root>
                        </div>
                    </div>
                </div>
            </AlertNotification.Content>
            <AlertNotification.AlertNotificationClose />
        </AlertNotification.Root>
    );
};

export { AutoAddressesMetricCard };
